import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Mail, Send, CheckCircle, AlertCircle } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { API_ENDPOINTS, buildApiUrl } from '../config/apiConfig';
import { trackEvent } from '../lib/analytics';
import './ContactUsPage.css';

const MAX_MESSAGE = 8000;

function ContactUsPage() {
  const { t, isRTL } = useLanguage();
  const location = useLocation();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [hp, setHp] = useState('');
  const [status, setStatus] = useState('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const appliedEnterpriseRef = useRef(false);

  useEffect(() => {
    if (location.state?.inquiry === 'enterprise_plan' && !appliedEnterpriseRef.current) {
      appliedEnterpriseRef.current = true;
      setSubject(t('contactForm.subject.enterprise'));
    }
  }, [location.state, t]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (hp.trim() !== '') return;
    setErrorMsg('');
    const n = name.trim();
    const em = email.trim();
    const su = subject.trim();
    const msg = message.trim();
    if (!n || !em || !su || !msg) {
      setStatus('error');
      setErrorMsg(t('contactForm.error.required'));
      return;
    }
    if (msg.length > MAX_MESSAGE) {
      setStatus('error');
      setErrorMsg(t('contactForm.error.messageLength'));
      return;
    }
    setStatus('submitting');
    try {
      const res = await fetch(buildApiUrl(API_ENDPOINTS.CONTACT_INQUIRY), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: n, email: em, subject: su, message: msg, hp: '' }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setStatus('error');
        if (res.status === 429) {
          setErrorMsg(t('contactForm.error.rateLimit'));
        } else {
          setErrorMsg(
            data.code === 'EMAIL_NOT_CONFIGURED'
              ? t('contactForm.error.notConfigured')
              : data.error || t('contactForm.error.generic')
          );
        }
        return;
      }
      trackEvent('contact_submit', { page: '/contact', metadata: { source: 'inquiry_form' } });
      setStatus('success');
      setName('');
      setEmail('');
      setSubject('');
      setMessage('');
    } catch {
      setStatus('error');
      setErrorMsg(t('contactForm.error.generic'));
    }
  };

  return (
    <div className={`contact-us-page ${isRTL ? 'rtl' : 'ltr'}`}>
      <div className="contact-us-container">
        <header className="contact-us-header">
          <h1>{t('contactForm.title')}</h1>
          <p className="contact-us-subtitle">{t('contactForm.subtitle')}</p>
        </header>

        <div className="contact-us-grid">
          <form className="contact-us-card" onSubmit={handleSubmit} noValidate>
            <div className="contact-form-honeypot" aria-hidden="true">
              <label htmlFor="contact-hp">Website</label>
              <input
                id="contact-hp"
                type="text"
                name="company_website"
                autoComplete="off"
                tabIndex={-1}
                value={hp}
                onChange={(e) => setHp(e.target.value)}
              />
            </div>

            <div className="contact-form-field">
              <label htmlFor="contact-name">{t('contactForm.name')}</label>
              <input
                id="contact-name"
                type="text"
                name="name"
                required
                autoComplete="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={200}
                className="contact-input"
              />
            </div>
            <div className="contact-form-field">
              <label htmlFor="contact-email">{t('contactForm.email')}</label>
              <input
                id="contact-email"
                type="email"
                name="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                maxLength={256}
                className="contact-input"
              />
            </div>
            <div className="contact-form-field">
              <label htmlFor="contact-subject">{t('contactForm.subject')}</label>
              <input
                id="contact-subject"
                type="text"
                name="subject"
                required
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                maxLength={200}
                className="contact-input"
              />
            </div>
            <div className="contact-form-field">
              <label htmlFor="contact-message">{t('contactForm.message')}</label>
              <textarea
                id="contact-message"
                name="message"
                required
                rows={6}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                maxLength={MAX_MESSAGE}
                className="contact-textarea"
              />
              <span className="contact-char-hint">
                {message.length} / {MAX_MESSAGE}
              </span>
            </div>

            {status === 'error' && errorMsg && (
              <div className="contact-alert contact-alert--error" role="alert">
                <AlertCircle size={18} className="contact-alert-icon" />
                <span>{errorMsg}</span>
              </div>
            )}
            {status === 'success' && (
              <div className="contact-alert contact-alert--success" role="status">
                <CheckCircle size={18} className="contact-alert-icon" />
                <span>{t('contactForm.success')}</span>
              </div>
            )}

            <button
              type="submit"
              className="contact-submit"
              disabled={status === 'submitting'}
            >
              {status === 'submitting' ? (
                t('contactForm.sending')
              ) : (
                <>
                  <Send size={18} className="contact-submit-icon" />
                  {t('contactForm.submit')}
                </>
              )}
            </button>
          </form>

          <aside className="contact-info-card">
            <div className="contact-info-icon-wrap">
              <Mail size={28} strokeWidth={1.5} />
            </div>
            <h2 className="contact-info-title">{t('contactForm.infoTitle')}</h2>
            <a className="contact-mail-link" href="mailto:info@yimnakai.com">
              info@yimnakai.com
            </a>
            <p className="contact-info-note">{t('contactForm.infoNote')}</p>
          </aside>
        </div>
      </div>
    </div>
  );
}

export default ContactUsPage;
